import { useMutation } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * 로그인된 admin 의 self-service 보안 설정 (비밀번호 변경 + 휴대폰 2FA 등록).
 * 해당 endpoint 들은 아직 생성 OpenAPI 스펙에 없어 raw fetch 로 호출한다
 * (useTwoFactorMutations 와 동일 패턴). BE 계약은 be-user 와 조율 (H-1/H-2).
 *
 * 응답 envelope 의 error.code 를 그대로 throw 하여 화면에서 분기 메시지를 띄운다.
 */
const PASSWORD_PATH = '/admin/v1/me/password';
const PHONE_2FA_BASE = '/admin/v1/me/2fa/phone';

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface PhoneRequestCodeResult {
  maskedPhone?: string;
  expiresInSeconds?: number;
  dispatchStatus?: string;
}

export interface PhoneVerifyResult {
  enrolled?: boolean;
  maskedPhone?: string;
}

/** 인증 헤더는 adminFetch 가 붙인다 — 여기서는 쓰기 요청의 멱등키만 얹는다. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

const postJson = async <T>(path: string, body: unknown): Promise<T> => {
  const res = await adminFetch(path, {
    method: 'POST',
    headers: writeHeaders('POST', path, body),
    body: JSON.stringify(body),
  });
  const env = (await res.json().catch(() => null)) as
    | { success?: boolean; data?: T; error?: { code?: string } }
    | null;
  if (!res.ok || env?.success === false) {
    throw new Error(env?.error?.code ?? `HTTP_${res.status}`);
  }
  return (env?.data ?? ({} as T)) as T;
};

export const useChangePasswordMutation = () =>
  useMutation<void, Error, ChangePasswordRequest>({
    mutationFn: (body) => postJson<void>(PASSWORD_PATH, body),
  });

export const usePhone2faRequestMutation = () =>
  useMutation<PhoneRequestCodeResult, Error, { phone: string }>({
    mutationFn: (body) => postJson<PhoneRequestCodeResult>(`${PHONE_2FA_BASE}/request`, body),
  });

export const usePhone2faVerifyMutation = () =>
  useMutation<PhoneVerifyResult, Error, { code: string }>({
    mutationFn: (body) => postJson<PhoneVerifyResult>(`${PHONE_2FA_BASE}/verify`, body),
  });
